import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments } from '@modernloop/shared/components';
import { formatDateTimeRange } from '@modernloop/shared/datetime';
import { TimeIcon, UserOptionalIcon } from '@modernloop/shared/icons';
import { Avatar, Box, Stack, Typography } from '@mui/material';

import { DebriefContentDetailsInterviewer_DebriefFragment, ResponseStatus } from 'src/generated/mloop-graphql';

import getResponseStatusNode from 'src/entities/common/getResponseStatusNode';

type Fragments = {
  attendee: DebriefContentDetailsInterviewer_DebriefFragment;
};

type Props = {
  timezone: string;
};

const DebriefContentDetailsInterviewer: FCWithFragments<Fragments, Props> = ({ attendee, timezone }) => {
  const { t } = useTranslation('hiring-dashboard-schedule');
  return (
    <Stack direction="row" justifyContent="space-between" key={attendee.interviewerId}>
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                src={attendee.interviewer?.internalProfilePictureUrl || ''}
                alt={attendee.interviewer?.fullName || ''}
              >
                {attendee.interviewer?.fullName?.charAt(0)}
              </Avatar>
              <Typography variant="body2">{attendee.interviewer?.fullName}</Typography>
            </Stack>
          </Stack>

          {attendee.isOptional && <UserOptionalIcon tooltip={t('schedule.content.optional')} />}
        </Stack>
        {attendee.proposedTime && (
          <Stack direction="row" alignItems="center" gap={1}>
            <Box width="20px" />
            <TimeIcon />
            <Typography
              data-testid="debrief-content-details-interviewer-proposed-time"
              variant="body2"
              color="text.secondary"
            >
              {t('schedule.content.proposalLabel')}{' '}
              {formatDateTimeRange({
                start: attendee.proposedTime.startAt,
                end: attendee.proposedTime.endAt,
                timezone,
                showTzAbbr: true,
              })}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" alignItems="center">
        {getResponseStatusNode(attendee.responseStatus || ResponseStatus.NeedsAction, t)}
      </Stack>
    </Stack>
  );
};

DebriefContentDetailsInterviewer.fragments = {
  attendee: gql`
    fragment DebriefContentDetailsInterviewer_debrief on ApplicationDebriefAttendee {
      applicationDebriefId
      interviewerId
      interviewer {
        id
        fullName
        internalProfilePictureUrl
      }
      responseStatus
      isOptional
      proposedTime {
        startAt
        endAt
      }
    }
  `,
};

export default DebriefContentDetailsInterviewer;
